import React, { useMemo } from "react";
import useSWR from "swr";
import { Line } from "react-chartjs-2";
import { CircularProgress } from "@material-ui/core";
import { apiReports } from "../api/reports";

// Day-within-window: the windows start on the last day of the previous month,
// so day 1 is that prior-month day and each month's line ends one day short of
// the month's final day (it counts toward the following month, matching the
// Received card). 31 slots cover every period this chart compares.
const DAYS = 31;

// Cumulate per-day counts into a length-31 array aligned to day-within-window.
// `stopAt` truncates the current (incomplete) month so its line ends at today.
function buildCumulative(points, stopAt = DAYS) {
  const byDay = {};
  (points || []).forEach((p) => {
    byDay[p.day] = (byDay[p.day] || 0) + p.count;
  });
  const out = [];
  let cum = 0;
  for (let d = 1; d <= stopAt; d++) {
    cum += byDay[d] || 0;
    out.push(cum);
  }
  // Pad the rest with null so chart.js breaks the line after the last real day.
  for (let d = stopAt + 1; d <= DAYS; d++) {
    out.push(null);
  }
  return out;
}

// Pair colors: the "current month" pair (current + same-month-last-year) is
// blue, the "previous month" pair (last month + last month last year) is green.
// Solid = this year, dashed = last year. Robust year-round.
const COLOR_CURRENT = "#00acdc"; // blue
const COLOR_PREVIOUS = "#94ba33"; // green

const IncomingMonthlyTrend = () => {
  const { data: response, error } = useSWR("incoming-monthly-trend", () =>
    apiReports.getIncomingMonthlyTrend()
  );

  const loading = !response && !error;
  const r = response?.data?.result;

  const chartData = useMemo(() => {
    if (!r) {return null;}
    const todayDay = r.todayDay || DAYS;

    const datasets = [
      {
        label: r.lastMonthLastYear?.label || "Last month last year",
        data: buildCumulative(r.lastMonthLastYear?.points),
        borderColor: COLOR_PREVIOUS,
        backgroundColor: COLOR_PREVIOUS,
        borderDash: [6, 4],
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.2,
        fill: false,
      },
      {
        label: r.lastYearSameMonth?.label || "Last year same month",
        data: buildCumulative(r.lastYearSameMonth?.points),
        borderColor: COLOR_CURRENT,
        backgroundColor: COLOR_CURRENT,
        borderDash: [6, 4],
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.2,
        fill: false,
      },
      {
        label: r.lastMonth?.label || "Last month",
        data: buildCumulative(r.lastMonth?.points),
        borderColor: COLOR_PREVIOUS,
        backgroundColor: COLOR_PREVIOUS,
        borderWidth: 2.5,
        pointRadius: 0,
        tension: 0.2,
        fill: false,
      },
      {
        label: r.current?.label || "Current",
        data: buildCumulative(r.current?.points, todayDay),
        borderColor: COLOR_CURRENT,
        backgroundColor: COLOR_CURRENT,
        borderWidth: 3,
        pointRadius: 0,
        tension: 0.2,
        fill: false,
      },
    ];

    return {
      labels: Array.from({ length: DAYS }, (_, i) => String(i + 1)),
      datasets,
    };
  }, [r]);

  const options = useMemo(
    () => ({
      maintainAspectRatio: false,
      legend: { position: "top" },
      tooltips: {
        mode: "index",
        intersect: false,
        callbacks: {
          title: (items) => `Day ${items[0].label}`,
          label: (item, data) => {
            const v = item.yLabel;
            if (v == null) {return null;}
            const label = data.datasets[item.datasetIndex].label;
            return `${label}: ${Number(v).toLocaleString()}`;
          },
        },
      },
      scales: {
        xAxes: [
          {
            scaleLabel: { display: true, labelString: "Day within month window" },
            ticks: { autoSkip: true, maxTicksLimit: 12 },
          },
        ],
        yAxes: [
          {
            ticks: { beginAtZero: true, callback: (v) => v.toLocaleString() },
            scaleLabel: { display: true, labelString: "Cumulative incoming inspections" },
          },
        ],
      },
    }),
    []
  );

  return (
    <div className="tw-mb-10">
      <h4 className="tw-text-xl tw-font-bold">Incoming Inspections by Month</h4>
      <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
        Cumulative daily totals — current month, previous month, and the same two months last year
      </p>
      <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4" style={{ height: "360px" }}>
        {loading ? 
          <div className="tw-flex tw-items-center tw-justify-center tw-h-full">
            <CircularProgress />
          </div>
          : chartData ? 
            <Line data={chartData} options={options} />
            : 
            <p className="tw-text-gray-500 tw-text-sm">No data available.</p>
        }
      </div>
    </div>
  );
};

export default IncomingMonthlyTrend;